/**
 * DomainDashboardClient
 * SignalR client for connecting to domain-specific dashboard hubs
 * Supports real-time metrics streaming with subscription-based updates
 */
import { BaseSignalRClient } from '../shared/BaseSignalRClient';
/**
 * Client for a specific domain dashboard hub
 * Connects to /hubs/dashboard/{domain} on the dashboards WebSocket service
 */
export class DomainDashboardClient extends BaseSignalRClient {
    constructor(baseUrl, domain, getToken, options = {}) {
        const hubUrl = `${baseUrl}/hubs/dashboard/${domain}`;
        super(hubUrl, getToken, options);
        this.domain = domain;
    }
    /**
     * Register server-to-client event handlers
     */
    setupServerMethods() {
        if (!this.connection)
            return;
        // Primary metrics event - sent on subscribe, refresh, and periodic updates
        this.connection.on('ReceiveDashboardMetrics', (metrics) => {
            this.emit('metrics', metrics);
        });
        // Subscription confirmation
        this.connection.on('Subscribed', (confirmation) => {
            this.emit('subscribed', confirmation);
        });
        // Unsubscription confirmation
        this.connection.on('Unsubscribed', () => {
            this.emit('unsubscribed', undefined);
        });
        // Initial badge snapshot pushed by BaseDashboardHub.OnConnectedAsync
        // and by an explicit RequestBadgeSnapshot call on reconnect.
        this.connection.on('BadgeSnapshot', (snapshot) => {
            this.emit('badgeSnapshot', snapshot);
        });
        // Incremental badge deltas pushed by RabbitMQ-driven event consumers.
        this.connection.on('BadgeDelta', (delta) => {
            this.emit('badgeDelta', delta);
        });
        // TaskManagement activity drawer. One composed comment/note row addressed to
        // this user; there is no matching snapshot — the REST feed is the baseline.
        this.connection.on('ActivityDelta', (delta) => {
            this.emit('activityDelta', delta);
        });
        // Module presence: who currently has TaskManagement open.
        this.connection.on('PresenceSnapshot', (message) => {
            this.emit('presenceSnapshot', message);
        });
        this.connection.on('PresenceJoined', (message) => {
            this.emit('presenceJoined', message);
        });
        this.connection.on('PresenceLeft', (message) => {
            this.emit('presenceLeft', message);
        });
    }
    /**
     * Ask the server for the current presence roster. Shares the hub's
     * RequestBadgeSnapshot entry point, which also re-joins the presence group —
     * a reconnect gets a fresh connection id and loses its group membership.
     */
    async requestPresenceSnapshot() {
        await this.invoke('RequestBadgeSnapshot');
    }
    /** The user IDs currently viewing the module, fetched on demand. */
    async getPresentUsers() {
        return (await this.invoke('GetPresentUsers')) ?? [];
    }
    /**
     * Ask the server to re-send the badge snapshot. NOTE: the SDK does NOT call this automatically —
     * `BaseSignalRClient.onreconnected` only flips connection state, it does not re-invoke server
     * methods. After a reconnect the consumer must re-subscribe and call this itself to resync.
     */
    async requestBadgeSnapshot() {
        await this.invoke('RequestBadgeSnapshot');
    }
    /**
     * Subscribe to dashboard metrics with optional filter
     * @param filter - Optional filter for department/location/date range
     * @param refreshInterval - Server-side refresh interval in seconds (5-300)
     */
    async subscribe(filter, refreshInterval = 30) {
        await this.invoke('SubscribeToDashboard', filter || null, refreshInterval);
    }
    /**
     * Unsubscribe from dashboard metrics
     */
    async unsubscribe() {
        await this.invoke('UnsubscribeFromDashboard');
    }
    /**
     * Request an immediate refresh of metrics
     */
    async refresh() {
        await this.invoke('RefreshDashboard');
    }
    /**
     * Get current metrics snapshot
     */
    async getCurrentMetrics() {
        return await this.invoke('GetCurrentMetrics');
    }
    /**
     * Get chart data by method name (domain-specific)
     * e.g., 'GetTasksByStatusChart', 'GetProjectsByStatusChart'
     */
    async getChart(methodName) {
        return await this.invoke(methodName);
    }
}
//# sourceMappingURL=DomainDashboardClient.js.map